import { ALL_SLUGS } from './content/catalogue.js';
import { DOC_SLUGS } from './content/docs.js';

/**
 * Routing — plan §12.1.
 *
 * Deliberately hand-rolled rather than TanStack Router or React Router. These
 * are static content pages prerendered to HTML: no nested layouts, no loaders,
 * no dynamic params beyond a slug. A router would add ~12 KB gzip to a landing
 * page budgeted at 30 KB TOTAL, in exchange for features none of these pages
 * use. That trade is worth revisiting if the app ever grows real nested routes.
 */

export type Route =
  | { readonly kind: 'home' }
  | { readonly kind: 'tool'; readonly slug: string }
  | { readonly kind: 'doc'; readonly slug: string }
  | { readonly kind: 'editor'; readonly toolId: string | null }
  /**
   * A share link — plan §16.4.
   *
   * Deliberately NOT in `ALL_ROUTES`: it takes a random id, so there is
   * nothing to prerender, and it must never be indexed. The `_headers` file
   * marks `/s/*` noindex for the same reason.
   */
  | { readonly kind: 'receive' }
  | { readonly kind: 'notFound'; readonly path: string };

export function matchRoute(pathname: string, search = ''): Route {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/') return { kind: 'home' };

  if (path === '/app') {
    const toolId = new URLSearchParams(search).get('tool');
    return { kind: 'editor', toolId };
  }

  if (/^\/s\/[0-9a-f]{32}$/.test(path)) return { kind: 'receive' };

  const slug = path.slice(1);
  if (ALL_SLUGS.includes(slug)) return { kind: 'tool', slug };
  if (DOC_SLUGS.includes(slug)) return { kind: 'doc', slug };

  return { kind: 'notFound', path };
}

/** Every URL the prerender step must emit, and the sitemap must list. */
export const ALL_ROUTES: readonly string[] = [
  '/',
  ...ALL_SLUGS.map((s) => `/${s}`),
  ...DOC_SLUGS.map((s) => `/${s}`),
];

/**
 * There is deliberately no client-side `navigate`.
 *
 * One existed, exported and called by nothing. Every link in the content zone
 * is a plain `<a href>` and a full navigation is correct for them: each tool
 * page is an independently prerendered document with its own CSP and its own
 * COOP/COEP zone, and pushState would keep the previous page's headers while
 * showing the next page's content.
 */
